import {
  StalenessStatus,
  type StudyMissionSelectDto,
  type StudyRunCaseDashboardMissionDto,
  type StudyRunCaseSelectDto,
  type StudySelectDto,
} from '@@types/api.types';
import { BlueLabel } from '@components/shared/blue-label.component';
import { EmptyStateContainer } from '@components/shared/empty-state-container.component';
import { Badge } from '@components/ui/badge';
import { Button } from '@components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@components/ui/select';
import { Skeleton } from '@components/ui/skeleton';
import {
  useGetMeiaRunCaseBriefAnalysis,
  useGetMeiaRunCaseDashboard,
  useGetMeiaRunCases,
  useGetMeiaScoringOutputs,
  useGetMeiaStudyMissionThreads,
  useGetMeiaStudyP4E2eRows,
  useGetMeiaStudySwimlaneTasks,
  useGetMeiaStudyTargetEngagements,
  useGetMeiaStudyThreadSwimlanes,
  useGetMeiaStudyThreatTargets,
  useGetMeiaStudyValidationResults,
  useGetMeiaTargetEffectOverrides,
  useGetMeiaTasksTechnologies,
  useGetMeiaThreatTargetSystems,
} from '@queries/meia.query';
import { ArrowLeftIcon, CheckIcon } from 'lucide-react';
import { type FC, useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { meiaRoute } from '../meia.constants';
import { getMeiaStudyThreadsLabel } from './meia-mission-display.util';
import { ALL_MISSIONS_FILTER_VALUE } from './meia-mission-filter-pills.component';
import { MeiaMissionScopeStrip } from './meia-mission-scope-strip.component';
import { buildEngagementMissionIndex } from './meia-p3-engagement.util';
import { formatRunCaseLabel } from './meia-p4-run-cases.util';
import { type BriefAnalysisTab, BriefAnalysisTile } from './meia-p5-brief-analysis-tabs.component';
import { BriefAssumptionsTile } from './meia-p5-brief-assumptions-tile.component';
import { MeiaP5BriefCoverTile } from './meia-p5-brief-cover-tile.component';
import { BriefRecommendationTile } from './meia-p5-brief-recommendation-tile.component';
import { BriefRisksTile } from './meia-p5-brief-risks-tile.component';
import { BriefSignoffTile } from './meia-p5-brief-signoff-tile.component';
import { BriefSyncMatrixTile } from './meia-p5-brief-sync-matrix.component';
import { BriefTargetCoverageTile } from './meia-p5-brief-target-coverage-tile.component';
import {
  buildBriefRiskItems,
  buildTargetCoverageRows,
  formatBriefEnumLabel,
  getBriefStudyVerdict,
  getBriefThreadPhaseRollupRows,
} from './meia-p5-brief.util';
import { MeiaPhaseAdvanceBar } from './meia-phase-advance-bar.component';
import { MeiaThreadTabsTile } from './meia-thread-tabs-tile.component';
import {
  type BriefRunCaseSelectionMode,
  resolveSelectedRunCaseId,
  useMeiaP5BriefSelection,
  useRequestedRunCaseId,
} from './use-meia-p5-brief-selection.hook';

type MeiaP5BriefProps = {
  isMissionsLoading?: boolean;
  onAddDecisionMaker?: () => void;
  onOpenAcrRegister?: (focusEntryId?: string | null) => void;
  runCaseSelectionMode?: BriefRunCaseSelectionMode;
  missions: StudyMissionSelectDto[];
  study: StudySelectDto;
  studyId: string;
};

const buildSyncThreadOptions = (
  missions: StudyRunCaseDashboardMissionDto[],
): Array<{ label: string; value: string }> => [
  { label: 'All Threads', value: 'all' },
  ...missions.flatMap((mission) =>
    mission.threadVerdicts.map((thread) => ({
      label: `${mission.missionName} / ${thread.threadName}`,
      value: thread.threadId,
    })),
  ),
];

const findSyncThreadStages = (params: {
  missions: StudyRunCaseDashboardMissionDto[];
  threadId: string;
}): StudyRunCaseDashboardMissionDto['threads'][number] | null => {
  const { missions, threadId } = params;
  for (const mission of missions) {
    const thread = mission.threads.find((candidate) => candidate.threadId === threadId);
    if (thread) return thread;
  }
  return null;
};

const BriefHeader: FC<{
  onSelectRunCase: (runCaseId: string) => void;
  runCases: StudyRunCaseSelectDto[];
  selectedRunCaseId?: string | null;
  stalenessStatus?: StalenessStatus;
}> = (props) => {
  const { onSelectRunCase, runCases, selectedRunCaseId, stalenessStatus } = props;

  return (
    <div className="flex flex-wrap items-end justify-between gap-3">
      <div>
        <BlueLabel
          className="text-xs"
          text="Phase 5 — Decision Maker Brief"
        />
        <h2 className="mt-1 text-lg font-semibold text-gray-950">Brief assembly · sync matrix · sign-off</h2>
      </div>
      <div className="flex items-center gap-2">
        <span className="text-xs text-gray-500">Run case</span>
        <Select
          onValueChange={onSelectRunCase}
          value={selectedRunCaseId ?? ''}
        >
          <SelectTrigger className="h-9 w-56 bg-white text-xs">
            <SelectValue placeholder="Select run case" />
          </SelectTrigger>
          <SelectContent>
            {runCases.map((runCase) => (
              <SelectItem
                key={runCase.id}
                value={runCase.id}
              >
                {formatRunCaseLabel({ includeEpoch: true, runCase })}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {stalenessStatus && (
          <Badge
            size="xs"
            variant={stalenessStatus === StalenessStatus.CURRENT ? 'success' : 'warning'}
          >
            {formatBriefEnumLabel(stalenessStatus)}
          </Badge>
        )}
      </div>
    </div>
  );
};

export const MeiaP5Brief: FC<MeiaP5BriefProps> = (props) => {
  const {
    isMissionsLoading,
    onAddDecisionMaker,
    onOpenAcrRegister,
    runCaseSelectionMode = 'selectable',
    missions,
    study,
    studyId,
  } = props;
  const [activeAnalysisTab, setActiveAnalysisTab] = useState<BriefAnalysisTab>('traceability');
  const [eotMissionFilter, setEotMissionFilter] = useState(ALL_MISSIONS_FILTER_VALUE);
  const [selectedSyncThreadId, setSelectedSyncThreadId] = useState('all');

  const { data: runCases = [], isLoading: isRunCasesLoading } = useGetMeiaRunCases({ studyId });
  const shouldLoadBriefContent = runCases.length > 0;
  const { data: scoringOutputs = [], isLoading: isScoringOutputsLoading } = useGetMeiaScoringOutputs({
    enabled: shouldLoadBriefContent,
    studyId,
  });
  const { data: e2eRows = [] } = useGetMeiaStudyP4E2eRows({
    enabled: shouldLoadBriefContent,
    studyId,
  });
  const { data: threatTargets = [], isLoading: isTargetsLoading } = useGetMeiaStudyThreatTargets({
    enabled: shouldLoadBriefContent,
    studyId,
  });
  const { data: targetEffectOverrides = [], isLoading: isTargetEffectOverridesLoading } =
    useGetMeiaTargetEffectOverrides({ studyId });
  const { data: threatTargetSystems = [], isLoading: isThreatTargetSystemsLoading } = useGetMeiaThreatTargetSystems();
  const { data: validationResults = [], isLoading: isValidationLoading } = useGetMeiaStudyValidationResults({
    enabled: shouldLoadBriefContent,
    studyId,
  });
  const { threads: allStudyThreads } = useGetMeiaStudyMissionThreads({ missions, studyId });

  // Resolve the run-case id from URL/defaults so the dashboard query can fire before
  // the full selection hook (which depends on the dashboard response) is called.
  const requestedRunCaseId = useRequestedRunCaseId();
  const dashboardRunCaseId = useMemo(
    () => resolveSelectedRunCaseId({ requestedRunCaseId, runCaseSelectionMode, runCases, scoringOutputs }),
    [requestedRunCaseId, runCaseSelectionMode, runCases, scoringOutputs],
  );
  const {
    data: dashboard,
    isLoading: isDashboardLoading,
    isPlaceholderData: isDashboardPlaceholderData,
  } = useGetMeiaRunCaseDashboard({
    enabled: shouldLoadBriefContent,
    runCaseId: dashboardRunCaseId ?? undefined,
    studyId,
  });
  const {
    activeMissionDashboard,
    activeThreadDashboard,
    activeThreadId,
    activeThreadVerdict,
    handleSelectMission,
    handleSelectRunCase,
    handleSelectThread,
    selectedMissionId,
    selectedRunCase,
    selectedRunCaseId,
    selectedScoringOutput,
  } = useMeiaP5BriefSelection({
    dashboardMissions: dashboard?.missions ?? [],
    dashboardSelectedMissionId: dashboard?.activeMissionId,
    missions,
    runCaseSelectionMode,
    runCases,
    scoringOutputs,
  });
  // When the run case changes, the dashboard reloads while `selectedMissionId`/`activeThreadId`
  // may still hold a stale combination from the previous run case. The dashboard query keeps the
  // previous run case's data via `keepPreviousData` (so `isLoading` stays false during the switch),
  // so we must also treat placeholder data as inconsistent — otherwise the gate validates the new
  // selection against the OLD dashboard and lets an inconsistent (missionId, threadId, runCaseId)
  // triple reach the backend. Only fire once fresh dashboard data confirms the thread belongs to
  // the mission. A mission with no threads is also skipped — the scoring engine has nothing to
  // analyze and rejects it with NO_MISSION_THREADS.
  const isBriefSelectionConsistent = useMemo(() => {
    if (isDashboardLoading || isDashboardPlaceholderData) return false;
    const selectedMission = (dashboard?.missions ?? []).find((mission) => mission.missionId === selectedMissionId);
    if (!selectedMission) return false;
    if (selectedMission.threadVerdicts.length === 0) return false;
    if (!activeThreadId) return true;
    return selectedMission.threadVerdicts.some((thread) => thread.threadId === activeThreadId);
  }, [activeThreadId, dashboard?.missions, isDashboardLoading, isDashboardPlaceholderData, selectedMissionId]);
  const {
    data: briefAnalysis,
    isError: isBriefAnalysisError,
    isLoading: isBriefAnalysisLoading,
    isPlaceholderData: isBriefAnalysisPlaceholderData,
  } = useGetMeiaRunCaseBriefAnalysis({
    enabled: shouldLoadBriefContent && isBriefSelectionConsistent,
    missionId: selectedMissionId ?? undefined,
    runCaseId: selectedRunCaseId ?? undefined,
    studyId,
    threadId: activeThreadId ?? undefined,
  });
  // Effect on Target spans the whole study: every mission's threads feed the
  // engagement pipeline so the tile can filter per mission (or show all).
  const { isLoading: isTargetSwimlanesLoading, swimlanes: targetSwimlanes } = useGetMeiaStudyThreadSwimlanes({
    studyId,
    threads: allStudyThreads,
  });
  const { isLoading: isTargetTasksLoading, tasks: targetTasks } = useGetMeiaStudySwimlaneTasks({
    studyId,
    swimlanes: targetSwimlanes,
  });
  const { isLoading: isTargetTechnologiesLoading, taskTechnologies: targetTaskTechnologies } =
    useGetMeiaTasksTechnologies({
      enabled: shouldLoadBriefContent,
      studyId,
      tasks: targetTasks,
    });
  const { data: targetEngagements = [], isLoading: isTargetEngagementsLoading } = useGetMeiaStudyTargetEngagements({
    enabled: shouldLoadBriefContent,
    studyId,
  });

  const dashboardMissions = useMemo(() => dashboard?.missions ?? [], [dashboard?.missions]);
  const activeStages = activeThreadDashboard?.stageHeatmap ?? [];
  const briefVerdict = useMemo(() => getBriefStudyVerdict(dashboardMissions), [dashboardMissions]);
  const threadsLabel = useMemo(() => getMeiaStudyThreadsLabel(allStudyThreads), [allStudyThreads]);
  const risks = useMemo(
    () =>
      buildBriefRiskItems({
        gates: dashboard?.gateCards ?? [],
        scoringOutput: dashboard?.scoringOutput ?? selectedScoringOutput,
        stages: activeStages,
        validationResults,
      }),
    [activeStages, dashboard?.gateCards, dashboard?.scoringOutput, selectedScoringOutput, validationResults],
  );
  const eotMissionIdByTaskId = useMemo(
    () => buildEngagementMissionIndex({ swimlanes: targetSwimlanes, tasks: targetTasks, threads: allStudyThreads }),
    [allStudyThreads, targetSwimlanes, targetTasks],
  );
  const buildEotRows = useCallback(
    (missionFilter: string) =>
      buildTargetCoverageRows({
        // Study-level only — the per-alternative Effect-on-Target model is deferred (AZ-452), so the
        // alternative picker is hidden and every target is scored against the study-level model.
        architectureAlternativeId: null,
        engagements:
          missionFilter === ALL_MISSIONS_FILTER_VALUE
            ? targetEngagements
            : targetEngagements.filter((engagement) => eotMissionIdByTaskId.get(engagement.taskId) === missionFilter),
        overrides: targetEffectOverrides,
        targetSystems: threatTargetSystems,
        taskTechnologies: targetTaskTechnologies,
        threatTargets:
          missionFilter === ALL_MISSIONS_FILTER_VALUE
            ? threatTargets
            : threatTargets.filter((target) => target.missionId === missionFilter),
      }),
    [
      eotMissionIdByTaskId,
      targetEffectOverrides,
      targetEngagements,
      targetTaskTechnologies,
      threatTargetSystems,
      threatTargets,
    ],
  );
  const targetCoverageRows = useMemo(() => buildEotRows(eotMissionFilter), [buildEotRows, eotMissionFilter]);
  const targetCoverageTotalCount = useMemo(() => buildEotRows(ALL_MISSIONS_FILTER_VALUE).length, [buildEotRows]);
  const targetCoverageCountByMissionId = useMemo(
    () => new Map(missions.map((mission) => [mission.id, buildEotRows(mission.id).length])),
    [buildEotRows, missions],
  );
  const syncThreadOptions = useMemo(() => buildSyncThreadOptions(dashboardMissions), [dashboardMissions]);
  const syncRollupRows = useMemo(() => getBriefThreadPhaseRollupRows(dashboardMissions), [dashboardMissions]);
  const selectedSyncValue = syncThreadOptions.some((option) => option.value === selectedSyncThreadId)
    ? selectedSyncThreadId
    : 'all';
  const syncStages =
    selectedSyncValue === 'all'
      ? []
      : (findSyncThreadStages({ missions: dashboardMissions, threadId: selectedSyncValue })?.stageHeatmap ?? []);
  const scoringOutput = dashboard?.scoringOutput ?? selectedScoringOutput;
  const isBriefContentLoading =
    isRunCasesLoading || (shouldLoadBriefContent && (isScoringOutputsLoading || isDashboardLoading));

  if (isBriefContentLoading) {
    return <BriefLoadingState />;
  }

  if (!isRunCasesLoading && runCases.length === 0) {
    return (
      <EmptyStateContainer
        className="min-h-[520px] rounded-xl bg-white shadow-sm"
        action={
          <Button
            asChild
            variant="link"
          >
            <Link to={`/meia/${studyId}/p4?subtab=runcases`}>Open P4 Scoring</Link>
          </Button>
        }
        message="Create and run a case before assembling the Brief."
        title="No Run Cases"
      />
    );
  }

  return (
    <div className="flex min-w-0 flex-col gap-4">
      <BriefHeader
        onSelectRunCase={handleSelectRunCase}
        runCases={runCases}
        selectedRunCaseId={selectedRunCaseId}
        stalenessStatus={scoringOutput?.stalenessStatus}
      />

      <section>
        <MeiaP5BriefCoverTile
          missionsCount={missions.length}
          study={study}
          studyVerdict={briefVerdict}
          threadsLabel={allStudyThreads.length > 0 ? threadsLabel : undefined}
        />
      </section>

      {missions.length > 1 && (
        <MeiaMissionScopeStrip
          missions={missions}
          onSelectMission={handleSelectMission}
          phase="p5"
          selectedMissionId={selectedMissionId ?? undefined}
          study={study}
          threads={allStudyThreads}
        />
      )}

      {selectedRunCase && !isDashboardLoading && !dashboard?.scoringOutput ? (
        <EmptyStateContainer
          className="min-h-[320px] rounded-xl bg-white shadow-sm"
          action={
            <Button
              asChild
              variant="link"
            >
              <Link to={`/meia/${studyId}/p4?subtab=runcases`}>Open P4 Run Cases</Link>
            </Button>
          }
          message="Run the selected case before assembling a Brief."
          title="No current scoring output"
        />
      ) : (
        <>
          {selectedMissionId && (
            <MeiaThreadTabsTile
              canEdit={study.canEditStudy}
              missionId={selectedMissionId}
              onSelectThread={handleSelectThread}
              selectedThreadId={activeThreadId}
              studyId={studyId}
            />
          )}
          <section>
            <BriefRecommendationTile
              activeMission={activeMissionDashboard}
              activeThreadVerdict={activeThreadVerdict}
              briefVerdict={briefVerdict}
              mode="selected"
              risks={risks}
              stages={activeStages}
            />
          </section>
          <section className="flex flex-col gap-4">
            <BriefTargetCoverageTile
              countByMissionId={targetCoverageCountByMissionId}
              isLoading={
                isTargetsLoading ||
                isTargetEffectOverridesLoading ||
                isTargetEngagementsLoading ||
                isTargetSwimlanesLoading ||
                isTargetTasksLoading ||
                isTargetTechnologiesLoading ||
                isThreatTargetSystemsLoading
              }
              missions={missions}
              onSelectMission={setEotMissionFilter}
              rows={targetCoverageRows}
              selectedMissionId={eotMissionFilter}
              totalCount={targetCoverageTotalCount}
            />
          </section>
          <section>
            <BriefSyncMatrixTile
              isLoading={isDashboardLoading}
              onSelectThread={setSelectedSyncThreadId}
              rollupRows={syncRollupRows}
              selectedThreadId={selectedSyncValue}
              stages={selectedSyncValue === 'all' ? [] : syncStages}
              threadOptions={syncThreadOptions}
            />
          </section>
          <section>
            <BriefAnalysisTile
              activeTab={activeAnalysisTab}
              activeThreadId={activeThreadId}
              briefAnalysis={isBriefSelectionConsistent && !isBriefAnalysisPlaceholderData ? briefAnalysis : undefined}
              hasAnalysisError={isBriefSelectionConsistent && isBriefAnalysisError}
              isBriefAnalysisLoading={
                isBriefAnalysisLoading || (isBriefSelectionConsistent && isBriefAnalysisPlaceholderData)
              }
              isValidationLoading={isValidationLoading}
              onSelectTab={setActiveAnalysisTab}
              rows={e2eRows}
              stages={activeStages}
              validationResults={validationResults}
            />
          </section>
          <section className="grid gap-4 lg:grid-cols-2 lg:items-start">
            <BriefRisksTile
              fallbackMissionName={activeMissionDashboard?.missionName}
              fallbackThreadName={activeThreadVerdict?.threadName}
              risks={risks}
            />
            <BriefAssumptionsTile
              scoringOutput={scoringOutput}
              selectedRunCase={selectedRunCase}
              study={study}
            />
          </section>
          <section>
            <BriefSignoffTile
              authorName={study.ownerName}
              gates={dashboard?.gateCards ?? []}
              isLoading={isDashboardLoading || isValidationLoading || Boolean(isMissionsLoading)}
              onAddDecisionMaker={onAddDecisionMaker}
              onOpenAcrRegister={onOpenAcrRegister}
              scoringOutput={scoringOutput}
              study={study}
              studyId={studyId}
              validationResults={validationResults}
            />
          </section>

          <MeiaPhaseAdvanceBar
            backLink={{
              children: (
                <>
                  <ArrowLeftIcon className="size-4" /> Back to Scoring
                </>
              ),
              to: meiaRoute.studyTab({ studyId, tab: 'p4' }),
            }}
            caption="Approval happens in the Sign-off section once G1-G4 are satisfied."
            forwardLink={{
              children: (
                <>
                  Done <CheckIcon className="size-4" />
                </>
              ),
              to: meiaRoute.studyOverview(studyId),
            }}
            title="Brief status"
          />
        </>
      )}
    </div>
  );
};

const BriefLoadingState: FC = () => (
  <div className="flex min-w-0 flex-col gap-4">
    <Skeleton className="h-16 w-full rounded-xl" />
    <Skeleton className="h-40 w-full rounded-xl" />
    <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1fr)_360px] xl:items-start">
      <Skeleton className="h-44 w-full rounded-xl" />
      <Skeleton className="h-44 w-full rounded-xl" />
    </div>
    <Skeleton className="h-96 w-full rounded-xl" />
    <Skeleton className="h-72 w-full rounded-xl" />
  </div>
);
